import React, { useEffect, useState } from 'react';
import { FieldState } from 'final-form';
import { Field, FieldRenderProps, useForm } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { ErrorPalette } from '@tcl-boron-colors/colors';
import { FormTableFieldBase, FormTableFieldType } from '@tempus/patient-forms-service-shared';

import FormCheckbox from './FormCheckbox';
import FormDatePicker from './FormDatePicker';
import FormMultiSelectCheckbox from './FormMultiSelectCheckbox';
import FormSignature from './FormSignature';
import FormSignatureWithDate from './FormSignatureWithDate';
import FormSingleSelectDropdown from './FormSingleSelectDropdown';
import FormSingleSelectTypeahead from './FormSingleSelectTypeahead';
import FormTextArea from './FormTextArea';
import FormTextInput from './FormTextInput';
import FormUSPhoneInput from './FormUSPhoneInput';

import styles from './styles.module.css';
import classNames from 'classnames';

export type FormTableFieldFrontend = {
  name: string;
  props?: any;
  validate?: (value: any | undefined, allValues: any, meta: FieldState<any> | undefined) => any | undefined;
  readOnly?: boolean;
  conditionalValueConfig?: ConditionalValueConfig;
  validatedValueConfig?: ValidatedValueConfig;
};

export type FormTableField = FormTableFieldBase & FormTableFieldFrontend;

export type ConditionalValueConfigItem = {
  fieldNameToCheck: string;
  valueToCheck: any;
  conditionalLogic: (fieldValue: any, valueToCheck: any) => boolean;
};

export type ConditionalValueConfig = ConditionalValueConfigItem & {
  additionalConditions?: ConditionalValueConfigItem[];
};

export type ValidatedValueConfig = {
  validatedValueNames: string[];
  readOnly: boolean;
  concatenator?: string;
};

type FormTableProps = {
  fields: FormTableField[];
  values?: [any];
};

const fieldTypeMapper: Record<FormTableFieldType, React.ComponentType<FormTableField & FieldRenderProps<any>> & any> = {
  [FormTableFieldType.CHECK_BOX]: FormCheckbox,
  [FormTableFieldType.SIGNATURE]: FormSignature,
  [FormTableFieldType.SIGNATURE_WITH_DATE]: FormSignatureWithDate,
  [FormTableFieldType.TEXT_INPUT]: FormTextInput,
  [FormTableFieldType.SINGLE_SELECT_DROPDOWN]: FormSingleSelectDropdown,
  [FormTableFieldType.SINGLE_SELECT_TYPEAHEAD]: FormSingleSelectTypeahead,
  [FormTableFieldType.DATE_PICKER]: FormDatePicker,
  [FormTableFieldType.MULTI_SELECT_CHECKBOX]: FormMultiSelectCheckbox,
  [FormTableFieldType.TEXT_AREA]: FormTextArea,
  [FormTableFieldType.US_PHONE_INPUT]: FormUSPhoneInput,
};

const renderSubtext = (subtext: FormTableField['subText']) => {
  if (typeof subtext === 'function')
    return React.createElement(subtext, {
      classes: { ul: styles.tableRowTitleUl },
    });
  return subtext;
};

const renderFlexFormField = (field: FormTableField, index: number) => {
  return (
    <Field
      name={field.name}
      initialValue={field.props ? (field.props.initialValue ? field.props.initialValue : undefined) : undefined}
      render={(props) => {
        const [isError, setIsError] = useState<boolean>();

        useEffect(() => {
          const container = document.getElementById(`field-type-${props.input.name}`);
          setIsError(
            container
              ? !!props.meta.error && !!props.meta.touched && !container.contains(document.activeElement)
              : true,
          );
        }, [props.meta]);

        const Component = fieldTypeMapper[field.type];
        return (
          <div key={index} className={styles.tableRow}>
            <div
              className={classNames(
                styles.tableRowTitle,
                styles.tableCell,
                !field.label && !field.subText ? styles.tableRowLabelContainerEmpty : undefined,
              )}
            >
              <div className={styles.tableRowLabelContainer}>
                <div className={styles.tableRowTitleText}>{field.label}</div>
                {field.subText ? <div className={styles.tableRowSubText}>{renderSubtext(field.subText)}</div> : null}
              </div>
            </div>
            <div
              className={classNames(styles.tableRowContent, styles.tableCell)}
              style={{
                backgroundColor: !isError ? '#FFFFFF' : ErrorPalette[100],
              }}
              id={`field-type-${props.input.name}`}
            >
              <Component isError={isError} {...props} {...field.props} />
            </div>
          </div>
        );
      }}
      validate={field.validate}
      format={(value) => {
        if (typeof value === 'string') {
          return value.trim();
        }

        return value;
      }}
      formatOnBlur
    />
  );
};

const MultiCondition = ({
  conditionalValueConfig,
  children,
}: {
  conditionalValueConfig: ConditionalValueConfig;
  children: React.ReactNode;
}) => {
  const { fieldNameToCheck, valueToCheck, conditionalLogic, additionalConditions } = conditionalValueConfig;

  // Simple case - just one condition
  if (!additionalConditions || additionalConditions.length === 0) {
    return (
      <Field name={fieldNameToCheck} subscription={{ value: true }}>
        {({ input }) => (conditionalLogic(input.value, valueToCheck) ? children : null)}
      </Field>
    );
  }

  // Multiple conditions - create nested Field components
  const renderNestedConditions = (conditionIndex: number): React.ReactNode => {
    if (conditionIndex === -1) {
      // Check main condition first
      return (
        <Field name={fieldNameToCheck} subscription={{ value: true }}>
          {({ input }) => (conditionalLogic(input.value, valueToCheck) ? renderNestedConditions(0) : null)}
        </Field>
      );
    }

    if (conditionIndex >= additionalConditions.length) {
      return children;
    }

    const condition = additionalConditions[conditionIndex];
    return (
      <Field name={condition.fieldNameToCheck} subscription={{ value: true }}>
        {({ input }) =>
          condition.conditionalLogic(input.value, condition.valueToCheck)
            ? renderNestedConditions(conditionIndex + 1)
            : null
        }
      </Field>
    );
  };

  return <>{renderNestedConditions(-1)}</>;
};

const FormTable: React.FunctionComponent<FormTableProps> = ({ fields }) => {
  const form = useForm();

  return (
    <div className={styles.table}>
      {fields.map((field, index) => {
        if (field.conditionalValueConfig) {
          // Get all field names that should trigger form reset
          const fieldsToWatch = [field.conditionalValueConfig.fieldNameToCheck];
          if (field.conditionalValueConfig.additionalConditions) {
            fieldsToWatch.push(
              ...field.conditionalValueConfig.additionalConditions.map((condition) => condition.fieldNameToCheck),
            );
          }

          return (
            <React.Fragment key={index}>
              <MultiCondition conditionalValueConfig={field.conditionalValueConfig}>
                {renderFlexFormField(field, index)}
              </MultiCondition>
              {fieldsToWatch.map((fieldName) => (
                <OnChange key={`${field.name}-${fieldName}`} name={fieldName}>
                  {(value) => {
                    // Check if any condition is not met and reset field if needed
                    const mainConditionMet = field.conditionalValueConfig!.conditionalLogic(
                      value === fieldName
                        ? value
                        : form.getFieldState(field.conditionalValueConfig!.fieldNameToCheck)?.value,
                      field.conditionalValueConfig!.valueToCheck,
                    );

                    let additionalConditionsMet = true;
                    if (field.conditionalValueConfig!.additionalConditions) {
                      additionalConditionsMet = field.conditionalValueConfig!.additionalConditions.every(
                        (condition) => {
                          const conditionValue =
                            fieldName === condition.fieldNameToCheck
                              ? value
                              : form.getFieldState(condition.fieldNameToCheck)?.value;
                          return condition.conditionalLogic(conditionValue, condition.valueToCheck);
                        },
                      );
                    }

                    if (
                      (!mainConditionMet || !additionalConditionsMet) &&
                      form.getFieldState(field.name) &&
                      !field.props?.readOnly
                    ) {
                      form.change(field.name, '');
                      form.resetFieldState(field.name);
                    }
                  }}
                </OnChange>
              ))}
            </React.Fragment>
          );
        } else {
          return <React.Fragment key={index}>{renderFlexFormField(field, index)}</React.Fragment>;
        }
      })}
    </div>
  );
};

export default FormTable;
